import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  setLeadFilterField,
  applyFilters,
} from "@/features/leads/stores/leadFilterSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Shuffle,
  TrendingDown,
  TrendingUp,
  Building2,
  ArrowRight,
  HelpCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import axiosInstance from "@/lib/axios";
import { notificationEndpoint } from "@/config/endpoints";
import {
  SHUFFLE_LOGIN_SUMMARY_KEY,
  patchNotificationsRead,
} from "../api/useNotifications";
import type { ServerNotification } from "../types";

/**
 * Login-time shuffle accountability modal.
 *
 * On app boot it fetches the user's UNREAD `automation_shuffle` notifications
 * (the server coalesces them: one per automation per day) and, if any exist,
 * shows one modal summarising every shuffle that touched the user since they
 * last acknowledged.
 *
 * Two card shapes, switched on `payload.scope`:
 *   - personal / branch-manager → how many leads YOU lost / gained / had moved,
 *     plus per-branch lost/gained for the branches you manage.
 *   - admin (`scope === "admin"`) → COMPANY-WIDE: how many leads were reshuffled
 *     internally, how many detached to No Branch, plus the per-branch net across
 *     ALL branches. (Admins are excluded from the personal fan-out by design, so
 *     this is the card that makes the modal pop for a Super Admin.)
 *
 * "Seen" state is DB-backed, not localStorage: closing the modal marks the
 * notifications READ (they stay in the bell as read items), so the modal never
 * re-appears for them — on any device. Shown once per app boot.
 *
 * Fetched ONCE per boot: its key is off the bell's `["notifications"]` prefix
 * and nothing (socket or mutation) invalidates it — it used to refetch on
 * every inbox push for every recipient.
 */

const SHUFFLE_TYPE = "automation_shuffle";

export default function ShuffleSummaryModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector((s) => (s.auth as any)?.currentUser);
  const isAuthed = !!currentUser;

  // One showing per app boot — a mid-session refetch must not re-pop it.
  const shownRef = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  const { data } = useQuery({
    queryKey: SHUFFLE_LOGIN_SUMMARY_KEY,
    queryFn: async () => {
      const { data } = await axiosInstance.get(notificationEndpoint, {
        // `withCount=0`: the bell owns the unread badge; skip the count.
        params: { type: SHUFFLE_TYPE, unreadOnly: "true", limit: "20", withCount: "0" },
      });
      return (data?.data ?? []) as ServerNotification[];
    },
    enabled: isAuthed && !shownRef.current,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 0,
  });

  const cards = useMemo(() => data ?? [], [data]);
  const open = cards.length > 0 && !dismissed;
  if (open) shownRef.current = true;

  async function acknowledge() {
    setDismissed(true);
    // DB-backed "seen": mark each card read so it never re-pops (any device).
    const ids = cards.map((c) => c.id);
    await Promise.allSettled(
      ids.map((id) => axiosInstance.patch(`${notificationEndpoint}/${id}/read`))
    );
    // Bell cache patched in place — no list GET.
    patchNotificationsRead(ids);
  }

  function goToLeads(target: "lost" | "gained" | "moved") {
    void acknowledge();
    // lost → Revoked/Reassigned; moved → Revoked/All (assignment survives a
    // branch move, but branch scope hides the lead → active-but-hidden
    // bucket); gained → the active list.
    dispatch(setLeadFilterField({ key: "isRevoked", value: target !== "gained" }));
    dispatch(setLeadFilterField({ key: "isReenquired", value: false }));
    dispatch(
      setLeadFilterField({
        key: "revokeType",
        value: target === "lost" ? "reassigned" : "all",
      })
    );
    dispatch(applyFilters());
    if (!location.pathname.startsWith("/leads")) navigate("/leads");
  }

  if (!open) return null;

  const isAdminView = cards.some((c) => (c.payload as any)?.scope === "admin");

  const totalLost = cards.reduce((s, c) => s + (c.payload.lostCount ?? 0), 0);
  const totalGained = cards.reduce((s, c) => s + (c.payload.gainedCount ?? 0), 0);
  const totalMoved = cards.reduce((s, c) => s + (c.payload.movedCount ?? 0), 0);
  const totalReshuffled = cards.reduce(
    (s, c) => s + ((c.payload as any).reshuffledCount ?? 0),
    0
  );
  const totalNoBranch = cards.reduce(
    (s, c) => s + ((c.payload as any).noBranchCount ?? 0),
    0
  );

  const subtitle = isAdminView
    ? [
        totalReshuffled > 0 &&
          `${totalReshuffled} lead${totalReshuffled === 1 ? "" : "s"} reshuffled company-wide`,
        totalNoBranch > 0 && `${totalNoBranch} detached to No Branch`,
      ]
        .filter(Boolean)
        .join(" · ") || "Leads moved across your branches"
    : [
        totalLost > 0 && `You lost ${totalLost} lead${totalLost === 1 ? "" : "s"}`,
        totalGained > 0 &&
          `you gained ${totalGained} lead${totalGained === 1 ? "" : "s"}`,
      ]
        .filter(Boolean)
        .join(" · ") ||
      (totalMoved > 0
        ? "Some of your leads moved branch"
        : "Leads moved across branches you manage");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && void acknowledge()}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-[480px] max-h-[85vh] p-0 gap-0 rounded-[24px] border-none shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header — Xale standard: icon + title + subtitle + close */}
        <DialogHeader className="flex-row items-start text-left gap-3 space-y-0 px-5 pt-5 pb-4 border-b border-[#e6e8e7] bg-white">
          <div className="size-10 rounded-xl bg-[#eaf5f1] border border-[#c1e1d5] flex items-center justify-center shrink-0">
            <Shuffle size={18} className="text-[#218760]" strokeWidth={1.8} />
          </div>
          <div className="flex flex-col gap-[2px] flex-1 min-w-0">
            <p className="text-[20px] font-medium text-black tracking-[-0.8px] leading-tight">
              Your leads were reshuffled
            </p>
            <p className="text-[14px] text-[#505e59] leading-snug">{subtitle}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void acknowledge()}
            className="rounded-full shrink-0 -mr-1 -mt-1"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </Button>
        </DialogHeader>

        {/* One card per automation */}
        <div className="overflow-y-auto px-5 py-4 flex flex-col gap-3 bg-[#f8f8f8]">
          {cards.map((c) => {
            const p = c.payload as any;
            const isAdminCard = p?.scope === "admin";
            return (
              <div
                key={c.id}
                className="bg-white border border-[#e6e8e7] rounded-[14px] p-3.5 flex flex-col gap-2.5"
              >
                {/* Title + count badges */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-medium text-[#051912] truncate">
                    {p.automationName ?? "Automation"}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isAdminCard ? (
                      <>
                        {(p.reshuffledCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#eaf5f1] text-[#156548] text-[11px] font-medium">
                            <Shuffle className="size-2.5" />
                            {p.reshuffledCount} reshuffled
                          </span>
                        )}
                        {(p.noBranchCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-[#da2a46] text-[11px] font-medium">
                            <AlertTriangle className="size-2.5" />
                            {p.noBranchCount} to No Branch
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {(p.lostCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-red-50 text-[#da2a46] text-[11px] font-medium">
                            <TrendingDown className="size-2.5" />−{p.lostCount}
                          </span>
                        )}
                        {(p.gainedCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-[#eaf5f1] text-[#156548] text-[11px] font-medium">
                            <TrendingUp className="size-2.5" />+{p.gainedCount}
                          </span>
                        )}
                        {(p.movedCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium">
                            <Building2 className="size-2.5" />
                            {p.movedCount}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* The reason — condition in plain English */}
                {p.conditionText && (
                  <div className="flex gap-1.5 bg-[#FBFBF4] border border-[#EFEDDC] rounded-[10px] px-2.5 py-2">
                    <HelpCircle size={12} className="text-[#9A8F4B] shrink-0 mt-0.5" />
                    <p className="text-[12px] text-[#6E663A] leading-[1.45]">
                      {p.conditionText}
                    </p>
                  </div>
                )}

                {/* Personal: sample of what was lost */}
                {!isAdminCard && (p.lost?.length ?? 0) > 0 && (
                  <div className="flex flex-col gap-1">
                    {p.lost!.slice(0, 3).map((item: any, i: number) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 text-[12px] text-[#505e59]"
                      >
                        <TrendingDown className="size-3 text-red-400 shrink-0" />
                        <span className="font-medium text-[#051912] truncate max-w-[150px]">
                          {item.leadName ?? `Lead #${item.leadId ?? "?"}`}
                          {item.childName ? ` · ${item.childName}` : ""}
                        </span>
                        {item.toUserName && (
                          <>
                            <ArrowRight className="size-2.5 shrink-0 opacity-50" />
                            <span className="truncate">{item.toUserName}</span>
                          </>
                        )}
                      </div>
                    ))}
                    {(p.lostCount ?? 0) > 3 && (
                      <span className="text-[11px] text-[#697571]">
                        +{(p.lostCount ?? 0) - 3} more — see "View lost leads"
                      </span>
                    )}
                  </div>
                )}

                {/* Personal: sample of what moved branch */}
                {!isAdminCard && (p.moved?.length ?? 0) > 0 && (
                  <div className="flex flex-col gap-1">
                    {p.moved!.slice(0, 3).map((item: any, i: number) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 text-[12px] text-[#505e59]"
                      >
                        <Building2 className="size-3 text-amber-500 shrink-0" />
                        <span className="font-medium text-[#051912] truncate max-w-[150px]">
                          {item.leadName ?? `Lead #${item.leadId ?? "?"}`}
                        </span>
                        <span className="truncate">{item.fromBranchName ?? "—"}</span>
                        <ArrowRight className="size-2.5 shrink-0 opacity-50" />
                        <span className="truncate">{item.toBranchName ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Per-branch net — manager (their branches) or admin (all) */}
                {(p.branchDeltas?.length ?? 0) > 0 && (
                  <div className="flex flex-col gap-1 border-t border-[#f0f2f1] pt-2">
                    <span className="text-[10.5px] font-semibold text-[#697571] uppercase tracking-wide">
                      {isAdminCard ? "Across all branches" : "Your branches"}
                    </span>
                    {p.branchDeltas!.map((b: any) => (
                      <div
                        key={b.branchId}
                        className="flex items-center gap-2 text-[12px]"
                      >
                        <Building2 className="size-3 text-[#697571] shrink-0" />
                        <span className="font-medium text-[#051912] truncate max-w-[160px]">
                          {b.branchName}
                        </span>
                        <span className="ml-auto flex items-center gap-1.5">
                          {b.lost > 0 && (
                            <span className="text-[#da2a46] font-semibold">−{b.lost}</span>
                          )}
                          {b.gained > 0 && (
                            <span className="text-[#156548] font-semibold">
                              +{b.gained}
                            </span>
                          )}
                          <span className="text-[#697571]">
                            net {b.gained - b.lost >= 0 ? "+" : ""}
                            {b.gained - b.lost}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer — Xale standard layout */}
        <DialogFooter className="flex-row items-center justify-between sm:justify-between gap-2 px-5 py-3.5 border-t border-[#e6e8e7] bg-white rounded-b-[24px]">
          <div className="flex items-center gap-2">
            {isAdminView
              ? totalReshuffled + totalNoBranch > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goToLeads("moved")}
                    className="gap-1.5 text-[#156548] hover:bg-[#eaf5f1] rounded-[10px]"
                  >
                    <Shuffle className="size-3.5" />
                    View reshuffled leads
                  </Button>
                )
              : (
                <>
                  {totalLost > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => goToLeads("lost")}
                      className="gap-1.5 text-[#da2a46] hover:bg-red-50 rounded-[10px]"
                    >
                      <TrendingDown className="size-3.5" />
                      View lost leads
                    </Button>
                  )}
                  {totalGained > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => goToLeads("gained")}
                      className="gap-1.5 text-[#156548] hover:bg-[#eaf5f1] rounded-[10px]"
                    >
                      <TrendingUp className="size-3.5" />
                      View gained leads
                    </Button>
                  )}
                  {totalMoved > 0 && totalLost === 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => goToLeads("moved")}
                      className="gap-1.5 text-amber-700 hover:bg-amber-50 rounded-[10px]"
                    >
                      <Building2 className="size-3.5" />
                      View moved leads
                    </Button>
                  )}
                </>
              )}
          </div>
          <Button
            variant="primary"
            onClick={() => void acknowledge()}
            className="px-6 rounded-[12px]"
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
